import { ProjectStatusFragment } from '@/gql/generated/graphql'
import { useCountdown } from '@/hooks/use-countdown'
import { cn } from '@/lib/utils'
import { formatTime } from '@utils/common'
import { DetailText } from 'design-configuration/components'
import React from 'react'

export const LiveCountdownTimer = ({
  project,
  dark,
  liveSaleEndTime,
  showSeconds = true
}: {
  project: ProjectStatusFragment
  dark?: boolean
  liveSaleEndTime?: Date
  showSeconds?: boolean
}) => {
  let endTime = project.auction_end_time ? new Date(project.auction_end_time) : undefined

  if (liveSaleEndTime) {
    endTime = liveSaleEndTime
  }

  const now = new Date()

  if (endTime && endTime <= now) {
    return null
  }

  return (
    <div
      className={cn(
        'px-2xs gap-x-2xs flex w-fit flex-row items-center',
        dark
          ? 'bg-surface-container-inverted text-on-surface-inverted fill-on-surface-inverted'
          : 'bg-surface-container text-on-surface fill-on-surface'
      )}
    >
      <DetailText className="font-medium">LIVE</DetailText>
      {endTime ? (
        <div className="flex">
          <Timer endTime={endTime} dark={dark} showSeconds={showSeconds} />
          <DetailText>remaining</DetailText>
        </div>
      ) : null}
    </div>
  )
}

const Timer = ({ endTime, dark, showSeconds }: { endTime?: Date | string; dark?: boolean; showSeconds: boolean }) => {
  const countdown = useCountdown(endTime)

  const weeks = countdown?.weeks ?? 0
  const days = countdown?.days ?? 0
  const hours = countdown?.hours ?? 0
  const minutes = countdown?.minutes ?? 0
  const seconds = countdown?.seconds ?? 0
  const timeLeft = countdown?.totalTimeLeftInMs ?? 0

  let timerValues = [
    {
      label: 'wk',
      value: weeks
    },
    {
      label: 'd',
      value: days
    },
    {
      label: 'hr',
      value: hours
    },
    {
      label: 'min',
      value: minutes
    }
  ]
  if (showSeconds || !minutes) {
    timerValues.push({
      label: 'sec',
      value: seconds
    })
  }

  if (weeks) {
    timerValues = timerValues.slice(0, 3)
  } else if (days) {
    timerValues = timerValues.slice(1, 4)
  } else if (hours) {
    timerValues = timerValues.slice(2, 5)
  } else if (minutes) {
    timerValues = timerValues.slice(3, 6)
  } else {
    timerValues = timerValues.slice(4, 6)
  }
  const TimerComponent = (time: { label: string; value: number }) => {
    return (
      <>
        <DetailText className={cn('mr-2xs', dark ? 'text-on-surface-inverted' : 'text-on-surface')}>
          {formatTime(time.value)}
        </DetailText>
        <DetailText className="mr-2xs">{time.label}</DetailText>
      </>
    )
  }

  if (timeLeft >= 0) {
    return (
      <div className="flex items-end">
        {timerValues.map((time) => (
          <div className="flex items-end" key={time.label}>
            {TimerComponent(time)}
          </div>
        ))}
      </div>
    )
  }

  return null
}
